import { processDispositionMetrics } from '@/lib/metrics'
import { SectionHeader } from './SectionHeader'
import { TransposedDataGrid } from './TransposedDataGrid'
import type { MetricsDataItem, DispositionMetricsItem } from '@/entities/dailyMetric'

interface DialerMetricsTableProps {
  metricsData: MetricsDataItem[]
  dispositionMetrics: DispositionMetricsItem[]
  selectedCampaign: { telephony: string | null } | null
}

export function DialerMetricsTable({
  metricsData,
  selectedCampaign,
  dispositionMetrics,
}: DialerMetricsTableProps) {
  // Check if campaign uses asterisk telephony
  if (!selectedCampaign || selectedCampaign.telephony !== 'asterisk') {
    return null
  }

  // Extract dialer metrics from metricsData
  const dialerData = metricsData
    .filter((item) => item.dialer_metrics)
    .map((item) => ({
      date: item.date,
      ...item.dialer_metrics!,
    }))

  if (dialerData.length === 0) {
    return null
  }

  // Get dates from dialer data
  const dates = dialerData.map((item) => item.date)

  // Get dialer statuses (exclude "Connected to AI Agent")
  const allStatuses = new Set<string>()
  dialerData.forEach((item) => {
    if (item.dialer_status) {
      Object.keys(item.dialer_status).forEach((status) => {
        if (status !== 'Connected to AI Agent') {
          allStatuses.add(status)
        }
      })
    }
  })

  // Get unconnected dispositions
  const { unconnectedTypes } = processDispositionMetrics(dispositionMetrics, metricsData)

  // Combine all status types
  const allStatusTypes = [...Array.from(allStatuses), ...unconnectedTypes]

  // Create rows for all statuses
  const statusRows = allStatusTypes
    .map((status, index) => {
      const row: Record<string, string | number> = {
        id: index,
        status: status,
      }

      let totalCount = 0
      let totalBase = 0

      // Check if this is a dialer status or disposition
      const isDialerStatus = allStatuses.has(status)

      dates.forEach((date) => {
        if (isDialerStatus) {
          // Get from dialer data
          const dayData = dialerData.find((item) => item.date === date)
          const count = dayData?.dialer_status?.[status] || 0
          const dateTotal = dayData?.total_dials || 0
          const percentage = dateTotal > 0 ? (count / dateTotal) * 100 : 0
          row[date] = percentage > 0 ? percentage.toFixed(2) : ''
          totalCount += count
          totalBase += dateTotal
        } else {
          // Get from disposition data
          const dayData = dispositionMetrics.find((item) => item.date === date)
          if (dayData) {
            const rawDispositions = dayData.rawDispositions || {}
            const count = rawDispositions[status] || 0
            // For unconnected dispositions, use total calls from dialer data
            const dialerDayData = dialerData.find((item) => item.date === date)
            const dateTotal = dialerDayData?.total_dials || 0
            const percentage = dateTotal > 0 ? (count / dateTotal) * 100 : 0
            row[date] = percentage > 0 ? percentage.toFixed(2) : ''
            totalCount += count
            totalBase += dateTotal
          }
        }
      })

      const overallPercentage = totalBase > 0 ? (totalCount / totalBase) * 100 : 0
      row.total = overallPercentage > 0 ? `${overallPercentage.toFixed(2)}%` : ''
      row._totalCount = totalCount // Store for filtering

      return row
    })
    .filter((row) => (row._totalCount as number) > 0) // Only show dispositions that occurred at least once

  // Create VOIP minutes row
  const voipRow: Record<string, string | number> = {
    id: statusRows.length,
    status: 'VOIP minutes',
  }

  let totalVoipMinutes = 0
  dates.forEach((date) => {
    const dayData = dialerData.find((item) => item.date === date)
    const voipMinutes = dayData?.voip_minutes || 0
    voipRow[date] = voipMinutes > 0 ? voipMinutes.toFixed(1) : ''
    totalVoipMinutes += voipMinutes
  })
  voipRow.total = totalVoipMinutes > 0 ? totalVoipMinutes.toFixed(1) : ''

  // Combine status rows with VOIP minutes row and reindex
  const rows = [...statusRows, voipRow].map((row, index) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _totalCount, ...cleanRow } = row
    return { ...cleanRow, id: index }
  })

  return (
    <div className="h-max-[600px] flex flex-col w-full mt-8">
      <SectionHeader title="Dialer Report" />
      <div className="bg-white rounded-lg shadow-sm border border overflow-hidden">
        <TransposedDataGrid
          rows={rows}
          firstColumn={{
            field: 'status',
            headerName: 'Dialer Status',
          }}
          lastColumn={{
            field: 'total',
            headerName: 'Total',
            type: 'number' as const,
          }}
          dates={dates}
        />
      </div>
    </div>
  )
}